import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Megaphone, Plus, Trash2, Search, Users, Mail, ArrowUpRight,
  Loader2, BarChart3, AlertTriangle,
} from 'lucide-react';

import { fmtDateEST } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function MarketingCampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ name: '', description: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchCampaigns = async () => {
    try {
      const [campRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/marketing/campaigns`, { withCredentials: true }),
        axios.get(`${API}/api/marketing/stats`, { withCredentials: true }),
      ]);
      setCampaigns(campRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCampaigns(); }, []);

  const handleCreate = async () => {
    if (!newCampaign.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/marketing/campaigns`, newCampaign, { withCredentials: true });
      toast.success('Campaign created');
      setShowCreate(false);
      setNewCampaign({ name: '', description: '' });
      navigate(`/admin/marketing/campaigns/${res.data.id}`);
    } catch (err) {
      toast.error('Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/api/marketing/campaigns/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Campaign deleted');
      setDeleteTarget(null);
      fetchCampaigns();
    } catch (err) {
      toast.error('Failed to delete campaign');
    }
  };

  const statusColors = {
    draft: 'bg-slate-100 text-slate-700',
    discovering: 'bg-blue-100 text-blue-700',
    enriching: 'bg-amber-100 text-amber-700',
    complete: 'bg-green-100 text-green-700',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-blue-600" />
            Marketing Lead Generator
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Discover, enrich, and promote leads to CRM
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2">
          <Plus className="h-4 w-4" /> New Campaign
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Campaigns', value: stats.total_campaigns, icon: Megaphone, color: 'text-blue-600' },
            { label: 'Total Leads', value: stats.total_leads, icon: Users, color: 'text-slate-600' },
            { label: 'Enriched', value: stats.total_enriched, icon: Search, color: 'text-amber-600' },
            { label: 'With Email', value: stats.total_with_email, icon: Mail, color: 'text-green-600' },
            { label: 'Promoted', value: stats.total_promoted, icon: ArrowUpRight, color: 'text-indigo-600' },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 font-medium">{label}</p>
                    <p className="text-2xl font-bold">{value}</p>
                  </div>
                  <Icon className={`h-5 w-5 ${color}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Megaphone className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">No campaigns yet</h3>
            <p className="text-sm text-slate-500 mb-4">
              Create your first campaign to start discovering leads
            </p>
            <Button onClick={() => setShowCreate(true)} className="gap-2">
              <Plus className="h-4 w-4" /> Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <Card
              key={c.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/admin/marketing/campaigns/${c.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-lg truncate">{c.name}</h3>
                      <Badge className={statusColors[c.status] || statusColors.draft}>
                        {c.status}
                      </Badge>
                    </div>
                    {c.description && (
                      <p className="text-sm text-slate-500 mt-1 truncate">{c.description}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">
                      Created {fmtDateEST(c.created_at)} by {c.created_by_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-6 ml-4">
                    <div className="text-center">
                      <p className="text-lg font-bold">{c.total_discovered}</p>
                      <p className="text-xs text-slate-500">Found</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-amber-600">{c.total_enriched}</p>
                      <p className="text-xs text-slate-500">Enriched</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-green-600">{c.total_with_email}</p>
                      <p className="text-xs text-slate-500">Emails</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-indigo-600">{c.total_promoted}</p>
                      <p className="text-xs text-slate-500">Promoted</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-400 hover:text-red-600 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Campaign</DialogTitle>
            <DialogDescription>
              Create a campaign to discover and enrich leads. You'll define search criteria next.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Campaign Name</Label>
              <Input
                placeholder="e.g., VP HR Texas Staffing"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                placeholder="Brief description of the target audience"
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="gap-2">
              {creating && <Loader2 className="h-4 w-4 animate-spin" />}
              Create & Configure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Campaign
            </DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{deleteTarget?.name}</strong> and all
              {' '}{deleteTarget?.total_discovered || 0} discovered leads. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete Campaign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
